import { useState } from 'react';
import { ordersApi } from '@hotjar/webapp/domains/billing/orders';
import { useCustomer } from '@hotjar/webapp/domains/billing/customers/api';
import { Loader, useNotify } from '@hotjar/hotsauce';
import { useMutation } from 'react-query';
import { InfoBox } from '@hotjar/webapp/components/InfoBox/InfoBox';
import { usePrettifyPrice } from '@hotjar/webapp/hooks/usePrettifyPrice';
import { supportsMultipleSku } from '@hotjar/webapp/domains/common/product/features';
import type { SubscriptionSummaryAccount } from '../SubscriptionSummary';
import { Header, SwitchLink } from '../SubscriptionSummary.styles';
import { FooterCTA } from './SubscriptionCost.styles';
import { CostBreakdown } from './Totals';

interface Props {
  account: SubscriptionSummaryAccount;
  onSubmit: (x: string) => void;
  hasPaidPlan: boolean;
}

export function ZuoraSubscriptionCosts({ account, onSubmit, hasPaidPlan }: Props): JSX.Element {
  const { showToast } = useNotify();
  const [cancelling, setCancelling] = useState(false);
  const { data: customer } = useCustomer(account?.id);
  const { prettifyPrice } = usePrettifyPrice();

  const isMonthlyBilling = customer?.subscription.billingPeriod === 'MONTHLY';
  const isYearlyBilling = customer?.subscription.billingPeriod === 'YEARLY';

  const title = <Header>Cost per {`${isMonthlyBilling ? 'month' : 'year'}`}</Header>;
  let footerCTA = null;

  const subscriptionTotal = customer?.currentPeriodCostSummary.amountWithoutTax ?? 0;
  const subscriptionVatRate = customer?.currentPeriodCostSummary.taxRate ?? 0;
  const subscriptionVatTotal = customer?.currentPeriodCostSummary.taxAmount ?? 0;
  const totalWithTax = customer?.currentPeriodCostSummary.amountWithTax ?? 0;

  const shouldShowVatRate = subscriptionTotal !== 0 && subscriptionVatRate !== 0;

  const cancelSubscription = useMutation(() => ordersApi.cancelSubscription(account.id as number), {
    onSuccess: () => {
      showToast({
        text: 'Successfully cancelled subscription',
        kind: 'success',
      });
      // give some time for the success toast to show and then reload to reflect the changes
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    },
    onError: () => {
      showToast({
        text: 'Sorry we failed to cancel your subscription',
        kind: 'danger',
      });
    },
    onMutate: () => setCancelling(true),
    onSettled: () => setCancelling(false),
  });

  if (!supportsMultipleSku(account) && !hasPaidPlan) {
    footerCTA = (
      <SwitchLink
        onClick={() => onSubmit('business')}
        variant="primary"
        data-testid="switch-monthly"
      >
        Get more insights with Hotjar Business
      </SwitchLink>
    );
  } else if (hasPaidPlan && (isYearlyBilling || isMonthlyBilling)) {
    footerCTA = (
      <FooterCTA>
        {customer?.subscription.status !== 'CANCELLED' && (
          <SwitchLink onClick={() => cancelSubscription.mutate()} variant="primary">
            <Loader isLoading={cancelling}>Cancel subscription</Loader>
          </SwitchLink>
        )}
        {isYearlyBilling && (
          <SwitchLink
            onClick={() => onSubmit('monthly')}
            variant="secondary"
            data-testid="switch-monthly"
          >
            Contact support to switch to monthly
          </SwitchLink>
        )}
      </FooterCTA>
    );
  }

  return (
    <InfoBox
      title={title}
      body={
        <CostBreakdown
          subscriptionTotal={subscriptionTotal}
          subscriptionVatRate={subscriptionVatRate}
          subscriptionVatTotal={subscriptionVatTotal}
          totalWithTax={totalWithTax}
          prettifyCost={prettifyPrice}
          hasPaidPlan={hasPaidPlan}
          shouldShowVatRate={shouldShowVatRate}
        />
      }
      footer={footerCTA}
    />
  );
}
